import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tv, Download as DownloadIcon, ShieldCheck, Smartphone, Wifi, ArrowRight, CheckCircle2, AlertTriangle, Cable } from "lucide-react";
import { Button } from "../components/ui/button";
import { NexsoLogo } from "../components/app/NexsoLogo";
import { ContactSupportLink } from "../components/app/ContactSupport";
import axios from "axios";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const bodyFont    = { fontFamily: "Manrope, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

const API = process.env.REACT_APP_BACKEND_URL;

export default function Download() {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/launcher/latest`, { withCredentials: true })
      .then((r) => setMeta(r.data))
      .catch((e) => setError(e?.message || "Failed to load"));
  }, []);

  const version = meta?.version || "1.2.0";
  const size = meta?.size_mb ?? 3.6;
  // Always proxy through our backend so the customer sees a direct file download.
  const downloadUrl = `${API}/api/launcher/download`;
  const changelog = meta?.changelog || [];
  // Server tells us whether an APK is actually available (uploaded or upstream).
  const available = meta?.available !== false;

  return (
    <div className="min-h-screen text-slate-900 relative overflow-x-hidden" style={{ background: "#FFFFFF", ...bodyFont }}>
      {/* Ambient blobs */}
      <div aria-hidden="true" className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full opacity-30" style={{ background: "radial-gradient(circle, #0EA5E9 0%, transparent 70%)", filter: "blur(80px)" }} />
        <div className="absolute top-[40%] right-[-10%] w-[34rem] h-[34rem] rounded-full opacity-25" style={{ background: "radial-gradient(circle, #38BDF8 0%, transparent 70%)", filter: "blur(80px)" }} />
      </div>

      <div className="relative z-10">
        {/* Navbar (minimal) */}
        <header
          className="fixed top-0 inset-x-0 z-50 border-b backdrop-blur-xl"
          style={{ background: "rgba(255,255,255,0.85)", borderColor: "#E2E8F0" }}
          data-testid="download-navbar"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="download-logo">
              <NexsoLogo size="md" />
              <span className="font-semibold text-xl tracking-tight text-slate-900" style={headingFont}>Nexso</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/login" data-testid="download-signin">
                <Button variant="ghost" className="rounded-full text-slate-700 hover:text-slate-900 hover:bg-slate-50">Sign in</Button>
              </Link>
              <Link to="/register" data-testid="download-getstarted">
                <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white px-5 shadow-md">Get started</Button>
              </Link>
            </div>
          </div>
        </header>

        <main className="pt-28 pb-24 px-6 md:px-10">
          <div className="max-w-5xl mx-auto">
            {/* Hero */}
            <div className="text-center mb-12">
              <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">Android TV launcher</div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold mb-5"
                style={headingFont}
                data-testid="download-heading"
              >
                Install Nexso on{" "}
                <span style={accentFont} className="text-sky-600">any Android TV</span>{" "}
                in 3 minutes.
              </h1>
              <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-8" data-testid="download-subheading">
                Sideload the Nexso launcher APK onto your Android stick, Smart TV or set-top box. No Google Play account needed. Works on Android 7.0 (Nougat) and newer.
              </p>

              {/* Primary CTA */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
                <a
                  href={available ? downloadUrl : "#"}
                  className={`inline-flex items-center gap-2 rounded-full px-8 h-14 text-base font-semibold shadow-lg transition-all ${available ? "bg-sky-500 hover:bg-sky-600 text-white hover:shadow-xl" : "bg-slate-200 text-slate-500 cursor-not-allowed"}`}
                  data-testid="download-apk-button"
                  download
                  onClick={(e) => { if (!available) e.preventDefault(); }}
                  aria-disabled={!available}
                >
                  <DownloadIcon className="h-5 w-5" />
                  {available ? `Download .APK · v${version}` : "APK coming soon"}
                </a>
                <a
                  href="#instructions"
                  className="inline-flex items-center gap-1.5 rounded-full px-6 h-14 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 border border-slate-200"
                  data-testid="download-instructions-link"
                >
                  See install instructions
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="text-xs text-slate-500 flex flex-wrap items-center justify-center gap-x-4 gap-y-1" data-testid="download-meta">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Signed APK · safe to sideload</span>
                <span>·</span>
                <span>{size} MB</span>
                <span>·</span>
                <span>Android 7.0+</span>
                {error && <><span>·</span><span className="text-rose-500">Couldn’t reach update server</span></>}
              </div>
            </div>

            {/* Step-by-step */}
            <section id="instructions" className="mt-16">
              <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold mb-8 text-center" style={headingFont} data-testid="instructions-heading">
                Get your TV online in <span style={accentFont} className="text-sky-600">3 steps</span>.
              </h2>

              <div className="grid md:grid-cols-3 gap-5">
                <StepCard
                  index={1}
                  title="Connect to Wi-Fi"
                  icon={<Wifi className="h-5 w-5 text-white" />}
                  testId="step-1"
                >
                  Plug your Android stick into the TV&apos;s HDMI port. Power it on and complete the Android setup wizard — connecting to your home Wi-Fi.
                </StepCard>
                <StepCard
                  index={2}
                  title="Allow side-loading"
                  icon={<Smartphone className="h-5 w-5 text-white" />}
                  testId="step-2"
                >
                  Go to <strong>Settings → Security &amp; restrictions → Install unknown apps</strong>. Toggle ON for the browser or File Manager you&apos;ll use to install the APK.
                </StepCard>
                <StepCard
                  index={3}
                  title="Open this page on the TV"
                  icon={<DownloadIcon className="h-5 w-5 text-white" />}
                  testId="step-3"
                >
                  On the TV, open a browser and visit <span className="font-mono text-sky-700">nexso.net/download</span>. Tap the big sky-blue Download button above. When the file finishes, tap it to install.
                </StepCard>
              </div>
            </section>

            {/* After install */}
            <section className="mt-16">
              <div
                className="rounded-3xl p-8 md:p-10 border backdrop-blur-xl"
                style={{ background: "linear-gradient(135deg, rgba(14,165,233,0.08), rgba(56,189,248,0.05))", borderColor: "rgba(14,165,233,0.30)" }}
              >
                <div className="text-2xl sm:text-3xl tracking-tight font-semibold mb-4" style={headingFont} data-testid="after-install-heading">
                  After install — <span style={accentFont} className="text-sky-600">pair your TV</span>
                </div>
                <ol className="space-y-3 text-slate-700 text-sm sm:text-base">
                  <li className="flex gap-3" data-testid="after-step-1">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                    <span>Open the <strong>Nexso</strong> app on your TV. It will display a 6-character pairing code &amp; QR.</span>
                  </li>
                  <li className="flex gap-3" data-testid="after-step-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                    <span>Sign in at <Link to="/login" className="text-sky-600 underline">nexso.net/login</Link> (or <Link to="/register" className="text-sky-600 underline">create an account</Link> — 7 days free, no card needed).</span>
                  </li>
                  <li className="flex gap-3" data-testid="after-step-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                    <span>Go to <strong>Dashboard → Screens → Pair a TV</strong>, scan the QR or enter the 6-character code.</span>
                  </li>
                  <li className="flex gap-3" data-testid="after-step-4">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                    <span>Pick a template, customize it, and publish to your screen instantly.</span>
                  </li>
                </ol>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link to="/register" data-testid="cta-register">
                    <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-md h-11 px-6 font-semibold">
                      Create account · 7 days free
                    </Button>
                  </Link>
                  <Link to="/login" data-testid="cta-login">
                    <Button variant="outline" className="rounded-full border-sky-300 text-sky-700 hover:bg-sky-50 h-11 px-6 font-semibold">
                      I already have an account
                    </Button>
                  </Link>
                </div>
              </div>
            </section>

            {/* Pro tips */}
            <section className="mt-14 grid md:grid-cols-2 gap-5">
              <Card icon={<Cable className="h-5 w-5 text-sky-600" />} title="Use ADB for bulk-install" testId="tip-adb">
                Deploying multiple sticks? Run our one-shot provisioning script — it installs the APK and sets Nexso as the default Home launcher in ~30 seconds per stick.{" "}
                <a className="text-sky-600 underline" href="https://github.com/emergent-nexso/nexso-launcher/blob/main/PROVISIONING.md" target="_blank" rel="noopener noreferrer">See the playbook →</a>
              </Card>
              <Card icon={<AlertTriangle className="h-5 w-5 text-amber-500" />} title="Stuck or locked out?" testId="tip-recovery">
                If you can&apos;t get past the Wi-Fi screen or your TV won&apos;t accept the install, follow our recovery guide. Most issues come from older Android versions silently blocking sideloads — we have a fix.{" "}
                <ContactSupportLink category="tv" subject="Stuck installing Nexso launcher" testId="download-contact-support-recovery">Contact support →</ContactSupportLink>
              </Card>
            </section>

            {/* Changelog */}
            {changelog.length > 0 && (
              <section className="mt-14" data-testid="changelog-section">
                <h3 className="text-lg font-semibold tracking-tight mb-4" style={headingFont}>What&apos;s new in v{version}</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  {changelog.map((c, i) => (
                    <li key={i} className="flex gap-2" data-testid={`changelog-item-${i}`}>
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t py-10 px-6 md:px-10" style={{ borderColor: "#E2E8F0" }} data-testid="download-footer">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center">
                <Tv className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-slate-700" style={headingFont}>Nexso</span>
              <span>© {new Date().getFullYear()}</span>
            </div>
            <div className="flex items-center gap-5">
              <Link to="/" className="hover:text-slate-900">Home</Link>
              <Link to="/login" className="hover:text-slate-900">Sign in</Link>
              <ContactSupportLink className="hover:text-slate-900" testId="download-footer-support">Support</ContactSupportLink>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

function StepCard({ index, title, icon, children, testId }) {
  return (
    <div
      className="rounded-2xl p-6 border backdrop-blur-xl bg-white/80"
      style={{ borderColor: "#E2E8F0" }}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center shadow-sm">
          {icon}
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-sky-600 font-medium">Step {index}</div>
      </div>
      <div className="text-lg font-semibold tracking-tight mb-2" style={headingFont}>{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}

function Card({ icon, title, children, testId }) {
  return (
    <div className="rounded-2xl p-6 border bg-white" style={{ borderColor: "#E2E8F0" }} data-testid={testId}>
      <div className="flex items-center gap-2.5 mb-2">
        {icon}
        <div className="text-base font-semibold tracking-tight" style={headingFont}>{title}</div>
      </div>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}
